import type { MentionContext, MentionResolver, ResolvedMention } from '../types'

type BrandRow = {
  id: string
  name: string
  website: string | null
  logo_url: string | null
}

function scoreBrand(name: string, query: string): number {
  const n = name.toLowerCase()
  const q = query.toLowerCase()
  if (!q) return 50
  if (n === q) return 100
  if (n.startsWith(q)) return 80 - (n.length - q.length)
  if (n.includes(q)) return 55
  // Subsequence fuzzy match — keeps ranking consistent with campaign +
  // video resolvers.
  let qi = 0
  for (const ch of n) {
    if (ch === q[qi]) qi++
    if (qi === q.length) break
  }
  if (qi === q.length) return 30 - (n.length - q.length)
  return 0
}

export const brandResolver: MentionResolver = async (query, ctx: MentionContext) => {
  const { data, error } = await ctx.supabase
    .from('brand_kits')
    .select('id, name, website, logo_url')
    .eq('workspace_id', ctx.workspaceId)
    .order('updated_at', { ascending: false })
    .limit(200)

  if (error) return { ok: false, error: `Could not load brands: ${error.message}` }

  const rows = ((data ?? []) as BrandRow[]).filter((r) => r.name && r.name.trim().length > 0)
  if (rows.length === 0) return { ok: true, candidates: [] }

  const scored = rows
    .map((row) => ({ row, score: scoreBrand(row.name, query) }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.row.name.localeCompare(b.row.name))
    .slice(0, 8)

  const candidates: ResolvedMention[] = scored.map(({ row }) => ({
    kind: 'brand',
    id: row.id,
    display: row.name,
    // Website is the most useful disambiguator when a workspace has
    // multiple brand kits with similar names (e.g. parent + child
    // brand). Falls through to no subtitle when website isn't set yet.
    subtitle: row.website ?? undefined,
    href: `/brand/${row.id}`,
    data: { id: row.id, name: row.name, website: row.website, logo_url: row.logo_url },
  }))

  return { ok: true, candidates }
}
